import React, { useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Title, Meta, Link } from 'react-head';
import { motion } from 'framer-motion';
import { Calendar, Clock, Tag } from 'lucide-react';
import AuthorBox from './components/AuthorBox/AuthorBox';
import ShareButtons from './components/ShareButtons/ShareButtons';
import RelatedPosts from './components/RelatedPosts/RelatedPosts';
import { getPostBySlug } from '../../../data/blogPosts';
import { getAuthorById } from '../../../data/authors';
import './BlogPost.css';

const BlogPost = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // If post not found, redirect to blog page
  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const author = getAuthorById(post.authorId);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <Title>{post.seo.metaTitle}</Title>
      <Meta name="description" content={post.seo.metaDescription} />
      <Meta name="keywords" content={post.seo.keywords.join(', ')} />
      <Meta name="author" content={author?.name || 'Trinity Financing'} />
      <Meta name="robots" content="index, follow" />
      <Link rel="canonical" href={`https://trinityfinancing.com/blog/${post.slug}`} />
      
      {/* Open Graph */}
      <Meta property="og:type" content="article" />
      <Meta property="og:url" content={`https://trinityfinancing.com/blog/${post.slug}`} />
      <Meta property="og:title" content={post.seo.metaTitle} />
      <Meta property="og:description" content={post.seo.metaDescription} />
      <Meta property="og:image" content={`https://trinityfinancing.com${post.featuredImage}`} />
      <Meta property="article:published_time" content={post.publishDate} />
      <Meta property="article:modified_time" content={post.lastUpdated} />
      <Meta property="article:author" content={author?.name} />
      
      {/* Twitter Card */}
      <Meta name="twitter:card" content="summary_large_image" />
      <Meta name="twitter:title" content={post.seo.metaTitle} />
      <Meta name="twitter:description" content={post.seo.metaDescription} />
      <Meta name="twitter:image" content={`https://trinityfinancing.com${post.featuredImage}`} />

      {/* Page Content */}
      <article className="blog-post-page">
        <div className="blog-post-container">
          {/* Header */}
          <motion.header 
            className="blog-post-header"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="blog-post-category-badge">{post.category}</div>
            <h1 className="blog-post-title">{post.title}</h1>

            <div className="blog-post-meta">
              <div className="blog-post-author-info">
                {author && (
                  <>
                    <img 
                      src={author.photo} 
                      alt={author.name}
                      className="blog-post-author-photo"
                    />
                    <div>
                      <div className="blog-post-author-name">{author.name}</div>
                      <div className="blog-post-author-title">{author.title}</div>
                    </div>
                  </>
                )}
              </div>

              <div className="blog-post-details">
                <span>
                  <Calendar size={16} />
                  {formatDate(post.publishDate)}
                </span>
                <span>
                  <Clock size={16} />
                  {post.readTime} min read
                </span>
              </div>
            </div>

            <ShareButtons post={post} />
          </motion.header>

          {/* Featured Image */}
          <motion.div
            className="blog-post-featured-image"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <img src={post.featuredImage} alt={post.title} />
          </motion.div>

          {/* Content */}
          <motion.div
            className="blog-post-content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="blog-post-tags">
              <Tag size={18} />
              {post.tags.map((tag, index) => (
                <span key={index} className="blog-post-tag">{tag}</span>
              ))}
            </div>
          )}

          {/* Share Buttons Bottom */}
          <ShareButtons post={post} position="bottom" />

          {/* Author Box */}
          <AuthorBox author={author} />

          {/* Related Posts */}
          <RelatedPosts currentPost={post} />
        </div>
      </article>
    </>
  );
};

export default BlogPost;